import { Controller } from 'stimulus'
import { useDispatch } from 'stimulus-use'
import { useBusItem } from '../mixins/useBusItem.js'

export default class extends Controller {
  static targets = ['checkbox', 'parentCheckbox', 'totalCheckboxesCheckedIndicator']

  static values = {
    checkboxesCheckedCount: Number,
    busItemId: String
  }

  checkboxesCheckedCountValueChanged() {
    if (this.hasCheckboxesCheckedCountValue) {
      this.dispatch(
        'checkboxesCheckedCountValueChanged',
        { checkboxesCheckedCount: this.checkboxesCheckedCountValue }
      )

      if (this.hasTotalCheckboxesCheckedIndicatorTarget) {
        this.totalCheckboxesCheckedIndicatorTarget.innerHTML = this.checkboxesCheckedCountValue
      }
    }
  }

  initialize() {
    useDispatch(this)
  }

  connect() {
    useBusItem(this)
    setTimeout(this.afterConnect.bind(this), 0)
  }

  disconnect() {
    this.removeFromBus()
  }

  afterConnect() {
    const checkboxes = this.checkboxTargets.map((checkbox) => { return new CheckboxItem(checkbox) })
    const updateState = () => {
      this.checkboxesCheckedCountValue = Object.keys(this._checkboxesList.checkedList).length
      this.updateParentToMatch(this._generalState)
    }
    this._checkboxesList = new CheckboxList(checkboxes, updateState.bind(this))
    this._parentCheckbox = new CheckboxItem(this.parentCheckboxTarget)
    updateState()
  }

  // Call this is in order to update a checkbox state.
  // So, after a child checkbox is updated, this should be called.
  updateCheckboxState(event) {
    const checkbox = new CheckboxItem(event.currentTarget)
    this._checkboxesList.updateCheckboxTracking(checkbox)
  }

  // This method refreshes the checkboxes observed to match the current checkboxTargets list.
  // Note:
  //  Since the controller is not observing dom changes,
  //  after a checkbox is deleted, added, disabled or enabled,
  //  this method needs to be called.
  refreshAll(event) {
    // matchGeneralState : Defines if child checkboxes should match general state.
    //                      Default value should be false.
    //                      For example:
    //                        - If matchGeneralState is true and generalState is 'allChecked' then
    //                          all child checkboxes will be checked after refresh.
    //                        - If matchGeneralState is true and generalState is 'noneChecked' then
    //                          all child checkboxes will be checked after refresh.
    //                        - If matchGeneralState is true and generalState is 'someChecked' then
    //                          nothing should happen. Although, if there are no child checkboxes checked after refresh
    //                          then the generalState should change to 'noneChecked' and the parent checkbox should be
    //                          updated accordingly.
    //
    //                        -  After referesh, if matchGeneralState is false then the 'general state' should be recalculated,
    //                           and the parent checkbox updated accordingly. This recalculation happens automatically after
    //                           updating the list of checkboxes. This method call <<this._checkboxesList.setList(checkboxes)>> triggers that.
    let matchGeneralState = false
    if (event.currentTarget.getAttribute) {
      matchGeneralState = (event.currentTarget.getAttribute(`data-${this.identifier}-match-general-state`) || 'false') === 'true'
    }
    let generalState
    if (matchGeneralState) {
      generalState = this._generalState
    }
    // Timeout is needed to force waiting on any dom changes to be applied
    setTimeout(() => {
      const checkboxes = this.checkboxTargets.map((checkbox) => { return new CheckboxItem(checkbox) })
      this._checkboxesList.setList(checkboxes)

      if (matchGeneralState && generalState && generalState !== 'SomeChecked') {
        this.updateParentToMatch(generalState)
        this.updateChildrenToMatch(generalState)
      }
    }, 0)
  }

  get _nextGeneralState() {
    switch (this._generalState) {
      case 'AllChecked':
        return 'NoneChecked'
      case 'NoneChecked':
        return 'AllChecked'
      case 'SomeChecked':
        return 'AllChecked'
    }
  }

  // This changes the generalState to the next one.
  // Order of states:
  // AllChecked -> NoneChecked -> SomeChecked -> AllChecked ...
  jumpToNextGeneralState(event) {
    this.updateChildrenToMatch(this._nextGeneralState)
  }

  updateParentToMatch(state) {
    switch (state) {
      case 'AllChecked':
        this._parentCheckbox.check()
        break
      case 'NoneChecked':
        this._parentCheckbox.uncheck()
        break
      case 'SomeChecked':
        if (this._parentCheckbox.isPartiallyChecked) { break }
        this._parentCheckbox.checkPartially()
        break
    }
  }

  // This method forces to update all the children checkboxes
  // into a particular state.
  updateChildrenToMatch(state) {
    switch (state) {
      case 'AllChecked':
        this._checkboxesList.checkAll()
        break
      case 'NoneChecked':
        this._checkboxesList.uncheckAll()
        break
    }
  }

  get checkboxesList() {
    return this._checkboxesList
  }

  // This calculates the general state based on the amount of checkboxesChecked
  // and the amount of checkboxes being tracked (amount of child checkboxes)
  get _generalState() {
    switch (Object.keys(this._checkboxesList.checkedList).length) {
      case 0:
        return 'NoneChecked'
      case this._checkboxesList.list.length:
        return 'AllChecked'
      default:
        return 'SomeChecked'
    }
  }
}

class CheckboxList {
  constructor(checkboxList, onCheckedListChangeCallback = function() { }) {
    this.list = checkboxList.filter(this.shouldTrack.bind(this))
    this._onCheckedListChangeCallback = onCheckedListChangeCallback
    this.checkedList = this._checkboxesChecked
  }

  setList(checkboxList) {
    this.list = checkboxList.filter(this.shouldTrack.bind(this))
    this.asureCheckedList()
  }

  asureCheckedList() {
    this.checkedList = this._checkboxesChecked
    this._onCheckedListChangeCallback()
  }

  updateCheckboxTracking(checkbox) {
    if (!this.shouldTrack(checkbox)) { return }
    if (checkbox.isChecked) { this.checkedList[checkbox.id] = checkbox }
    if (checkbox.isUnchecked) { delete this.checkedList[checkbox.id] }

    this._onCheckedListChangeCallback()
  }

  checkAll() {
    this.list.forEach((checkbox) => {
      checkbox.check()
    })
    this.asureCheckedList()
  }

  uncheckAll() {
    this.list.forEach((checkbox) => {
      checkbox.uncheck()
    })
    this.checkedList = {}
    this._onCheckedListChangeCallback()
  }

  // PRIVATE
  shouldTrack(checkbox) {
    return !checkbox.isDisabled
  }

  get _checkboxesChecked() {
    const checked = {}
    this.list.forEach((checkbox) => {
      if (this.shouldTrack(checkbox)) {
        if (checkbox.isChecked) {
          checked[checkbox.id] = checkbox
        }
      }
    })
    return checked
  }
}

class CheckboxItem {
  constructor(item) {
    this.item = item
  }

  get id() {
    return this.item.id
  }

  get isUnchecked() {
    return !this.isChecked
  }

  get isChecked() {
    return this.item.checked
  }

  get isPartiallyChecked() {
    return this.item.classList.contains(this._partiallyCheckedCssClass)
  }

  get isDisabled() {
    return this.item.hasAttribute('disabled')
  }

  assureCorrectStateAfterChangeEvent() {
    // Purposely not using 'isPartiallyChecked' here
    if (this.item.classList.contains(this._partiallyCheckedCssClass)) {
      // When the item was partially checked and was toggled, it must navigate to the 'checked' state
      this.check()
    } else if (this.isChecked) {
      this.check()
    } else {
      this.uncheck()
    }
  }

  check(item) {
    this.item.checked = true
    this.item.classList.remove(this._partiallyCheckedCssClass)
  }

  checkPartially(item) {
    this.item.checked = true
    this.item.classList.add(this._partiallyCheckedCssClass)
  }

  uncheck(item) {
    this.item.checked = false
    this.item.classList.remove(this._partiallyCheckedCssClass)
  }

  get _partiallyCheckedCssClass() {
    return 'c-checkbox__input--partially-checked'
  }
}
